import React from 'react';

import { Typography, TypographyProps } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { COLOR_DISABLED } from '~/components/App/theme';
import { getRequestOpenIssueCount } from '~/controllers/requests';
import { CACHE_TIME_20MIN } from '~/queryCache';

interface IIssueCount {
  requestId: string;
}

export const OpenIssueCount = <C extends React.ElementType>(props: TypographyProps<C, { component?: C } & IIssueCount>) => {
  const { requestId, sx, ...others } = props;
  const { isLoading, data: count } = useQuery(
    ['getRequestOpenIssueCount', { requestId }],
    getRequestOpenIssueCount,
    {
      // Disable this query - endpoint returns 404 for many requests
      enabled: false,
      // display previous value, if any
      cacheTime: CACHE_TIME_20MIN,
      // Keep data fresh for 20 minutes to avoid constant refetching
      staleTime: CACHE_TIME_20MIN,
      // Don't retry on failure (404s are expected for some requests)
      retry: false,
      // Don't refetch on window focus
      refetchOnWindowFocus: false,
      // Don't refetch on mount if we have stale data
      refetchOnMount: false,
      // Don't refetch on reconnect
      refetchOnReconnect: false,
      // Don't use error boundary
      useErrorBoundary: false,
      // Suppress error logging
      onError: () => {
        // Silently handle error - component will show "Not Available"
      },
    }
  );
  const issueCountNotAvailable = count === undefined || count === null;
  return (
    <Typography
      variant="subtitle1"
      sx={[{
        color: isLoading || issueCountNotAvailable ? COLOR_DISABLED : undefined,
      },
      ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...others}
    >
      {isLoading && issueCountNotAvailable ? 'Loading...' : issueCountNotAvailable ? 'Not Available' : `${count}`}
    </Typography>
  );
}
